import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Chip,
  CircularProgress, Alert, Button, Collapse, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { get } from '../../utils/api';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const ACCT = { icct: 'ICCT', kotilinga: 'Kotilinga' };

// Authoritative per-sub charge history (live), declines highlighted. Loads on expand.
function SubPayments({ account, subscriptionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr('');
      try { const r = await get('/api/manage/authnet-subscriptions/payments', { account, subscription_id: subscriptionId }); if (!cancel) setData(r); }
      catch (e) { if (!cancel) setErr(e?.response?.data?.error || e?.message || 'Failed to load'); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [account, subscriptionId]);
  if (loading) return <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={18} /></Box>;
  if (err) return <Alert severity="error" sx={{ my: 1 }}>{err}</Alert>;
  const ps = data?.payments || [];
  if (!ps.length) return <Typography variant="caption" color="text.secondary">No charge attempts recorded.</Typography>;
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mt: 1 }}>
      {ps.map((p, i) => (
        <Box key={p.trans_id || i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, px: 1.25, py: 0.6, borderBottom: 1, borderColor: 'divider', bgcolor: p.approved ? 'transparent' : '#fdecec' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>#{p.pay_num ?? '?'}{p.attempt > 1 ? ` (attempt ${p.attempt})` : ''} · {fmtDate(p.date)}</Typography>
            <Typography variant="caption" sx={{ display: 'block', color: p.approved ? 'text.secondary' : 'error.main' }}>
              {p.approved ? 'Approved' : 'Declined'}{p.response ? ` — ${p.response}` : ''}{p.order_number ? ` · Order ${p.order_number}` : (p.approved ? ' · no order yet' : '')}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtMoney(p.amount)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// Unified subscriptions view for one devotee: their mapped subs + allocation + failures + payment history.
export default function DevoteeSubscriptionsDialog({ open, onClose, customerId, data: dataProp, highlightProductId }) {
  const [data, setData] = useState(dataProp || null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true); setErr('');
    try { setData(await get('/api/manage/authnet-subscriptions/for-customer', { customer_id: customerId })); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load subscriptions'); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { if (open && !dataProp) load(); }, [open, dataProp, load]);
  useEffect(() => { if (dataProp) setData(dataProp); }, [dataProp]);

  const subs = (data?.subscriptions || []);
  const fundsHighlight = (s) => highlightProductId && (s.splits || []).some((sp) => String(sp.product_id) === String(highlightProductId));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AutorenewIcon color="primary" /><span>Subscriptions</span></Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          : err ? <Alert severity="error">{err}</Alert>
            : subs.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">No Authorize.Net subscriptions are mapped to this devotee.</Typography>
              </Box>
            ) : subs.map((s) => {
              const ek = `${s.account}:${s.subscription_id}`;
              const isOpen = !!expanded[ek];
              const hl = fundsHighlight(s);
              return (
                <Box key={ek} sx={{ border: hl ? 2 : 1, borderColor: s.failed_count > 0 ? 'error.light' : hl ? 'primary.main' : 'divider', borderRadius: 1, p: 1.25, mb: 1.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {s.sub_name || '(unnamed)'} <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{s.subscription_id}</Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={ACCT[s.account] || s.account} />
                        <Chip size="small" variant="outlined" label={`${fmtMoney(s.amount)} / installment`} />
                        <Chip size="small" color="success" variant="outlined" label={`${s.paid_count} paid`} />
                        {s.failed_count > 0 && <Chip size="small" color="error" icon={<WarningAmberIcon sx={{ fontSize: 14 }} />} label={`${s.failed_count} failed`} />}
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>Last paid {fmtDate(s.last_paid_date)}{s.last_pay_num ? ` · installment #${s.last_pay_num}` : ''}</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {(s.splits || []).map((sp, i) => (
                          <Typography key={i} variant="caption" sx={{ display: 'block', color: String(sp.product_id) === String(highlightProductId) ? 'primary.main' : 'text.secondary', fontWeight: String(sp.product_id) === String(highlightProductId) ? 600 : 400 }}>→ {sp.label} · {fmtMoney(sp.amount)}</Typography>
                        ))}
                      </Box>
                    </Box>
                    <Button size="small" endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setExpanded((e) => ({ ...e, [ek]: !e[ek] }))}>Payments</Button>
                  </Box>
                  <Collapse in={isOpen} unmountOnExit>
                    <Divider sx={{ my: 1 }} />
                    <SubPayments account={s.account} subscriptionId={s.subscription_id} />
                  </Collapse>
                </Box>
              );
            })}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
